import React from 'react';
import { makeObservable, observable, action, computed } from 'mobx';
import { observer } from 'mobx-react';

// Props interface
type TypewriterTextProps = {
  text: string;
  className?: string;
}

// TypewriterText as class component with embedded store
@observer
class TypewriterText extends React.Component<TypewriterTextProps> {
  @observable private displayText = '';
  @observable private isAnimating = false;
  @observable private currentPhase: 'idle' | 'removing' | 'adding' = 'idle';
  
  private targetText = '';
  private previousText = '';
  private animationFrame: number | null = null;
  private lastUpdateTime = 0;
  
  constructor(props: TypewriterTextProps) {
    super(props);
    makeObservable(this);
    this.targetText = props.text;
    this.displayText = props.text;
  }
  
  componentDidMount() {
    this.setText(this.props.text);
  }
  
  componentDidUpdate(prevProps: TypewriterTextProps) {
    if (prevProps.text !== this.props.text) {
      this.setText(this.props.text);
    }
  }
  
  componentWillUnmount() {
    this.cleanup();
  }
  
  @action
  private setText(newText: string) {
    if (this.targetText === newText) return;
    
    this.previousText = this.displayText;
    this.targetText = newText;
    this.isAnimating = true;
    this.currentPhase = this.previousText ? 'removing' : 'adding';
    
    if (this.animationFrame) {
      cancelAnimationFrame(this.animationFrame);
    }
    
    this.animate();
  }
  
  @action
  private animate = () => {
    const now = Date.now();
    const removeDelay = 10; // Fast removal
    const addDelay = 30; // Slower addition (LLM-like)
    
    if (this.currentPhase === 'removing') {
      if (now - this.lastUpdateTime >= removeDelay) {
        if (this.displayText.length > 0) {
          this.displayText = this.displayText.slice(0, -3);
          this.lastUpdateTime = now;
        } else {
          this.currentPhase = 'adding';
          this.lastUpdateTime = now;
        }
      }
      this.animationFrame = requestAnimationFrame(this.animate);
    } else if (this.currentPhase === 'adding') {
      if (now - this.lastUpdateTime >= addDelay) {
        if (this.displayText.length < this.targetText.length) {
          this.displayText = this.targetText.slice(0, this.displayText.length + 1);
          this.lastUpdateTime = now;
        } else {
          this.isAnimating = false;
          this.currentPhase = 'idle';
          return;
        }
      }
      this.animationFrame = requestAnimationFrame(this.animate);
    }
  }
  
  @computed
  private get showCursor() {
    return this.isAnimating && (this.currentPhase === 'adding' || this.currentPhase === 'removing');
  }
  
  private cleanup() {
    if (this.animationFrame) {
      cancelAnimationFrame(this.animationFrame);
    }
  }
  
  render() {
    const { className = '' } = this.props;
    
    return (
      <span className={`${className}`}>
        <span>{this.displayText}</span>
        {this.showCursor && (
          <span className="chat-item-text-cursor" />
        )}
      </span>
    );
  }
}

export default TypewriterText;